<template>
    <div>
        <b-alert :show="true" variant="warning" v-if="!benefitsResponse.processedStatus">
            <strong>Benefit Request Not Processed: </strong>
            {{ responseReasonLabel }}
        </b-alert>
        <template v-else>
            <b-alert :show="true" variant="info">
                <strong>
                    Pricing and coverage data is a point in time calculation based upon
                    the Quantity and Days Supply provided in the Request and may vary once
                    the prescription is filled. Any returned alternative information is
                    informational and not intended to replace clinical decisions.
                </strong>
            </b-alert>

            <b-table-simple v-if="benefits" bordered>
                <tr class="bg-light">
                    <th>Requested Medication</th>
                    <th v-if="hasFormularyStatus">Formulary</th>
                    <th>Alert</th>
                    <th>PA Required</th>
                    <th>Type</th>
                    <th>Day Supply</th>
                    <th>Quantity</th>
                    <th class="text-right">Estimated Patient Cost</th>
                </tr>
                <template v-for="mri in benefits.medicationRequestedInformation">
                    <tr v-for="(ps, i) in mri.patientSpecificPricingCoverages">
                        <td v-if="i === 0" :rowspan="mri.patientSpecificPricingCoverages.length">
                            {{ mri.medicationRequested.drugDescription }}
                        </td>
                        <td v-if="hasFormularyStatus">
                            {{ formularyStatusLabel(ps.pricingCoverage.formularyStatus) }}
                        </td>
                        <td>
                            <RTPBCoverageAlerts :pricing-coverage="ps.pricingCoverage" />
                        </td>
                        <td>
                            <RTPBPARequired :pricing-coverage="ps.pricingCoverage" />
                        </td>
                        <td>
                            <RTPBType :pharmacy="ps.pharmacy" />
                        </td>
                        <td>
                            {{ formattedDaySupply(ps.pricingCoverage) }}
                        </td>
                        <td>
                            {{ formattedQuantity(ps.pricingCoverage) }}
                        </td>

                        <td class="text-right">
                            <RTPBPatientCost :pricing-coverage="ps.pricingCoverage" />
                        </td>
                    </tr>
                </template>
                <tr class="bg-light"
                    v-if="benefits.medicationRequestedInformation.filter(mri => mri.alternativeMedicationInformation.length).length">
                    <th>Alternative Medication</th>
                    <th v-if="hasFormularyStatus">Formulary</th>
                    <th>Alert</th>
                    <th>PA Required</th>
                    <th>Type</th>
                    <th>Day Supply</th>
                    <th>Quantity</th>
                    <th class="text-right">Estimated Patient Cost</th>
                </tr>
                <template v-for="mri in benefits.medicationRequestedInformation">
                    <template v-for="ami in mri.alternativeMedicationInformation">
                        <tr v-for="(ps, i) in ami.patientSpecificPricingCoverages">
                            <td v-if="i === 0" :rowspan="ami.patientSpecificPricingCoverages.length">
                                {{ ami.alternativeMedication.drugDescription }}
                            </td>
                            <td v-if="hasFormularyStatus">
                                {{ formularyStatusLabel(ps.pricingCoverage.formularyStatus) }}
                            </td>
                            <td>
                                <RTPBCoverageAlerts :pricing-coverage="ps.pricingCoverage" />
                            </td>
                            <td>
                                <RTPBPARequired :pricing-coverage="ps.pricingCoverage" />
                            </td>
                            <td>
                                <RTPBType :pharmacy="ps.pharmacy" />
                            </td>
                            <td>
                                {{ formattedDaySupply(ps.pricingCoverage) }}
                            </td>
                            <td>
                                {{ formattedQuantity(ps.pricingCoverage) }}
                            </td>
                            <td class="text-right">
                                <RTPBPatientCost :pricing-coverage="ps.pricingCoverage" />
                            </td>
                        </tr>
                    </template>
                </template>
            </b-table-simple>

            <b-alert :show="benefitsResponse.reasonCode === 13" variant="warning">
                <strong>
                    {{ responseReasonLabel }}
                </strong>
            </b-alert>
        </template>
        <div class="text-right" v-if="requestMessageId">
            <small>
                <HelpPopup label="Message Info" icon="fa fa-info-circle" placement="topleft">
                    <div class="d-flex flex-column" style="gap: 6px;">
                        <div v-if="requestMessageId">
                            <small>
                                <div class="d-flex">
                                    <div class="flex-fill">
                                        Request Message Id:
                                    </div>
                                    <div>
                                        <a href="#" @click.prevent="copyText(requestMessageId, 'Request Message Id')">
                                            <i class="fa fa-copy"></i>
                                        </a>
                                    </div>
                                </div>
                                <a href="#" @click.prevent="copyText(requestMessageId, 'Request Message Id')">
                                    {{ requestMessageId }}
                                </a>
                            </small>
                        </div>
                        <div v-if="responseMessageId">
                            <small>
                                <div class="d-flex">
                                    <div class="flex-fill">
                                        Response Message Id:
                                    </div>
                                    <div>
                                        <a href="#" @click.prevent="copyText(responseMessageId, 'Response Message Id')">
                                            <i class="fa fa-copy"></i>
                                        </a>
                                    </div>
                                </div>
                                <a href="#" @click.prevent="copyText(responseMessageId, 'Response Message Id')">
                                    {{ responseMessageId }}
                                </a>
                            </small>
                        </div>
                    </div>
                </HelpPopup>
            </small>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import RTPBPatientCost from './RTPB/RTPBPatientCost.vue';
import RTPBType from './RTPB/RTPBType.vue';
import RTPBPARequired from './RTPB/RTPBPARequired.vue';
import RTPBCoverageAlerts from './RTPB/RTPBCoverageAlerts.vue';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import HelpPopup from '../HelpPopup.vue';

@Component({
    name: "RTPBComponent",
    components: {
        RTPBPatientCost,
        RTPBType,
        RTPBPARequired,
        RTPBCoverageAlerts,
        HelpPopup
    }
})
export default class RTPBComponent extends Vue {
    @Prop({ required: true }) protected benefits: any;
    @Prop({ default: "" }) protected requestMessageId!: string;
    @Prop({ default: "" }) protected responseMessageId!: string;

    private formattedQuantity(pricingCoverage: any) {
        const qty = pricingCoverage?.quantityPriced?.value;
        if (!qty) return "";
        const uom = pricingCoverage.quantityPriced?.quantityUnitOfMeasure?.description?.toLowerCase();
        return `${qty} ${uom}`;
    }

    private formattedDaySupply(pricingCoverage: any) {
        if (!pricingCoverage?.daysSupplyPriced) return "";
        return `${pricingCoverage.daysSupplyPriced} days`;
    }

    get hasFormularyStatus() {
        if (this.benefits.medicationRequestedInformation
            ?.some((i: any) => i.patientSpecificPricingCoverages
                ?.some((p: any) => p.pricingCoverage?.formularyStatus))) return true;
        if (this.benefits.medicationRequestedInformation
            ?.some((i: any) => i.alternativeMedicationInformation
                ?.some((p: any) => p.patientSpecificPricingCoverages
                    ?.some((ps: any) => ps.pricingCoverage?.formularyStatus)))) return true;
        return false;
    }

    private formularyStatusLabel(formularyStatus: string): string {
        const statusMap: Record<string, string> = {
            "U": "Unknown",
            "0": "Not Reimbursable",
            "1": "Non Formulary",
            "2": "On Formulary"
        };

        if (!formularyStatus) return "";

        return statusMap[formularyStatus] || `Preferred Level ${Number(formularyStatus) - 2}`;
    }

    get benefitsResponse() {
        return this.benefits.response;
    }

    get responseReasonLabel(): string {
        const { reasonCode, freeText } = this.benefitsResponse;

        const reasons: { [key: number]: string } = {
            0: "Discontinued NDC",
            1: "Drug Not Found",
            2: "Missing or Invalid Date of Birth",
            3: "Missing or Invalid Days Supply",
            4: "Missing or Invalid Gender Code",
            5: "Missing or Invalid Prescriber Id",
            6: "Missing or Invalid Quantity",
            7: "Not Processed",
            8: "PBM Member Id Missing or Invalid",
            9: "Patient Not Covered",
            10: "Patient Not Found",
            11: "Pharmacy Id Missing or Invalid",
            12: "Processed",
            13: "Alternatives Available If Desired",
            14: "System Error",
            15: "Unmatched Quantity Unit",
            16: "Validation Error"
        };

        const label: string = reasons[reasonCode] || "";
        return freeText ? `${label} (${freeText})` : label;
    }

    copyText(text: string, type: string) {
        navigator.clipboard.writeText(text);
        this.$notification(NotificationOptions.success(`Copied ${type} to clipboard`));
    }
}
</script>

<style scoped>
.table {
    font-size: .92em;
}
</style>